import React, { useState, useEffect } from 'react';
import { FiCopy, FiCode, FiFileText } from 'react-icons/fi';
import { extensionsAPI } from '../services/api';
import './API.css';

const API = () => {
  const [activeTab, setActiveTab] = useState('add-partner');
  const [copied, setCopied] = useState('');
  const [formData, setFormData] = useState({});
  const [extensions, setExtensions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // All available endpoints - based on actual implementation
  const allEndpoints = [
    { id: 'add-partner', name: 'Add Partner', method: 'POST', url: 'https://app.chromi.us/api/external/create-partner', role: 'partner' },
    { id: 'suspend-partner', name: 'Suspend Partner', method: 'POST', url: 'https://app.chromi.us/api/external/suspend-partner', role: 'partner' },
    { id: 'unsuspend-partner', name: 'Un-suspend Partner', method: 'POST', url: 'https://app.chromi.us/api/external/unsuspend-partner', role: 'partner' },
    { id: 'add-update-user', name: 'Add/Update User', method: 'POST', url: 'https://app.chromi.us/api/external/webhook/create-user', role: 'reseller' },
    { id: 'suspend-user', name: 'Suspend User', method: 'POST', url: 'https://app.chromi.us/api/external/webhook/suspend-user', role: 'reseller' },
    { id: 'unsuspend-user', name: 'Unsuspend User', method: 'POST', url: 'https://app.chromi.us/api/external/webhook/unsuspend-user', role: 'reseller' },
    { id: 'delete-user', name: 'Delete User', method: 'DELETE', url: 'https://app.chromi.us/api/external/webhook/delete-user', role: 'reseller' },
    { id: 'get-user', name: 'Get User', method: 'GET', url: 'https://app.chromi.us/api/external/webhook/get-user', role: 'reseller' }
  ];

  // Filter endpoints based on user role and partner type
  const apiEndpoints = allEndpoints.filter(endpoint => {
    if (!userRole) return true; // Show all while loading
    
    // Superadmin and Admin can see all endpoints
    if (userRole === 'superadmin' || userRole === 'admin') {
      return true;
    }
    
    // For partners, check partner type
    if (userRole === 'partner') {
      const partnerType = currentUser?.partnerType;
      
      // CEO and Whitelabeler can see all endpoints (Partner + End-User tabs)
      if (partnerType === 'ceo' || partnerType === 'whitelabeler') {
        return true;
      }
      
      // Reseller can only see End-User tabs
      if (partnerType === 'reseller') {
        return endpoint.role === 'reseller';
      }
    }
    
    // Default fallback
    return endpoint.role === userRole;
  });

  // Debug logging
  console.log('User Role:', userRole);
  console.log('Current User:', currentUser);
  console.log('Available Endpoints:', apiEndpoints);
  console.log('Active Tab:', activeTab);

  // Fetch user's role and extensions
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch('/api/auth/profile', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setUserRole(data.partner.role);
          setCurrentUser(data.partner);
        }
      } catch (error) {
        console.error('Error fetching user role:', error);
      }
    };

    const fetchExtensions = async () => {
      try {
        const data = await extensionsAPI.getAll();
        
        console.log('Extensions API response:', data);
        
        if (data.success && data.extensions) {
          console.log('Processed extensions array:', data.extensions);
          setExtensions(data.extensions);
        } else {
          console.error('Failed to fetch extensions:', data);
          setExtensions([]);
        }
      } catch (error) {
        console.error('Error fetching extensions:', error);
        setExtensions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
    fetchExtensions();
  }, []);

  // Auto-select first available tab when user data is loaded
  useEffect(() => {
    if (userRole && currentUser && apiEndpoints.length > 0) {
      // Check if current active tab is still available
      const isCurrentTabAvailable = apiEndpoints.some(endpoint => endpoint.id === activeTab);
      
      if (!isCurrentTabAvailable) {
        // Set to first available tab
        setActiveTab(apiEndpoints[0].id);
      }
    }
  }, [userRole, currentUser, apiEndpoints, activeTab]);



  // Update formData when currentUser changes to refresh token fields
  useEffect(() => {
    if (currentUser?._id) {
      setFormData(prev => ({
        ...prev,
        token: currentUser._id
      }));
    }
  }, [currentUser]);

  // Dynamic form field configurations - based on actual implementation
  const getFormFields = (endpointId) => {
    const baseFields = {
      token: { label: 'Token (PartnerID)', type: 'text', required: true, value: currentUser?._id || 'Error - Token Missing', placeholder: 'API Token', disabled: true }
    };

    const endpointFields = {
      'add-partner': {
        // Column 1
        token: { label: 'Token (PartnerID)', type: 'text', required: true, value: currentUser?._id || 'Error - Token Missing', placeholder: 'API Token', disabled: true },
        first_name: { label: 'First Name', type: 'text', required: true, value: 'Gary', placeholder: 'Ex. jon', disabled: true },
        phone_number: { label: 'Phone Number', type: 'text', required: false, value: '2015466542', placeholder: 'Ex. 289729857923', disabled: true },
        extensionTokens: { label: 'Extension Tokens', type: 'text', required: true, value: '', placeholder: 'Extension tokens', disabled: true },
        isAssignAll: { label: 'Assign All', type: 'checkbox', required: false, value: false, disabled: true },
        // Column 2
        email: { label: 'Email', type: 'email', required: true, value: 'somePartner@example.com', placeholder: 'Ex. abc@gmail.com', disabled: true },
        last_name: { label: 'Last Name', type: 'text', required: true, value: 'Snails', placeholder: 'Ex. vougn', disabled: true },
        planLevel: { label: 'Highest Plan Level', type: 'number', required: true, value: '5', placeholder: 'Ex. 5', disabled: true },
        balance: { label: 'Balance', type: 'number', required: true, value: '100', placeholder: 'Ex. 100', disabled: true },
        is_balance_unlimited: { label: 'Unlimited', type: 'checkbox', required: false, value: false, disabled: true }
      },
      'add-update-user': {
        // Column 1
        token: { label: 'Token (PartnerID)', type: 'text', required: true, value: currentUser?._id || 'Error - Token Missing', placeholder: 'API Token', disabled: true },
        first_name: { label: 'First Name', type: 'text', required: true, value: 'Gary', placeholder: 'Ex. John', disabled: false },
        phone_number: { label: 'Phone Number', type: 'text', required: false, value: '2015466542', placeholder: 'Ex. 1234567890', disabled: false },
        // Column 2
        email: { label: 'Email', type: 'email', required: true, value: 'someUser@example.com', placeholder: 'Ex. user@example.com', disabled: false },
        last_name: { label: 'Last Name', type: 'text', required: true, value: 'Snails', placeholder: 'Ex. Doe', disabled: false },
        ext_id: { label: 'Extension Token (Extension ID)', type: 'text', required: true, value: '', placeholder: 'Extension ID', disabled: false }
      },
      'suspend-partner': {
        // Column 1
        token: { label: 'Token', type: 'text', required: true, value: currentUser?._id || 'Error - Token Missing', placeholder: 'API Token', disabled: true },
        // Column 2
        email: { label: 'Email', type: 'email', required: true, value: 'somePartner@example.com', placeholder: 'Ex. partner@example.com', disabled: true }
      },
      'unsuspend-partner': {
        // Column 1
        token: { label: 'Token', type: 'text', required: true, value: currentUser?._id || 'Error - Token Missing', placeholder: 'API Token', disabled: true },
        // Column 2
        email: { label: 'Email', type: 'email', required: true, value: 'somePartner@example.com', placeholder: 'Ex. partner@example.com', disabled: true }
      },
      'suspend-user': {
        ...baseFields,
        email: { label: 'Email', type: 'email', required: true, value: 'someUser@example.com', placeholder: 'Ex. user@example.com', disabled: true }
      },
      'unsuspend-user': {
        ...baseFields,
        email: { label: 'Email', type: 'email', required: true, value: 'someUser@example.com', placeholder: 'Ex. user@example.com', disabled: true }
      },
      'delete-user': {
        ...baseFields,
        email: { label: 'Email', type: 'email', required: true, value: 'someUser@example.com', placeholder: 'Ex. user@example.com', disabled: true }
      },
      'get-user': {
        ...baseFields,
        email: { label: 'Email', type: 'email', required: true, value: 'someUser@example.com', placeholder: 'Ex. user@example.com', disabled: true }
      }
    };

    return endpointFields[endpointId] || baseFields;
  };

  // Generate dynamic request payload
  const generateRequestPayload = (endpointId) => {
    const fields = getFormFields(endpointId);
    const payload = {};
    
    if (endpointId === 'add-partner') {
      // Special handling for Add Partner with extension tokens
      payload.token = currentUser?._id || 'Error - Token Missing';
      payload.email = 'somePartner@example.com';
      payload.first_name = 'Gary';
      payload.last_name = 'Snails';
      payload.phone_number = '4439798008';
      payload.extensionTokens = formData.extensionTokens ? formData.extensionTokens.join(',') : '';
      payload.isAssignAll = formData.isAssignAll || false;
      payload.planLevel = parseInt(formData.planLevel) || 5;
      payload.balance = formData.balance !== undefined ? formData.balance : 100;
      payload.is_balance_unlimited = formData.is_balance_unlimited || false;
    } else if (endpointId === 'suspend-partner') {
      // Special handling for Suspend Partner
      payload.token = currentUser?._id || 'Error - Token Missing';
      payload.email = 'somePartner@example.com';
    } else if (endpointId === 'unsuspend-partner') {
      // Special handling for Un-suspend Partner
      payload.token = currentUser?._id || 'Error - Token Missing';
      payload.email = 'somePartner@example.com';
    } else {
      // Standard handling for other endpoints
      Object.keys(fields).forEach(key => {
        if (key === 'token') {
          payload[key] = currentUser?._id || 'Error - Token Missing';
        } else if (fields[key].type === 'checkbox') {
          payload[key] = fields[key].value;
        } else if (fields[key].type === 'select') {
          const value = formData[key] !== undefined ? formData[key] : fields[key].value;
          payload[key] = parseInt(value);
        } else {
          const value = formData[key] !== undefined ? formData[key] : fields[key].value;
          payload[key] = value;
        }
      });
      
      // Add plan field for add-update-user if extension has plans
      if (endpointId === 'add-update-user' && hasSelectedExtensionPlans() && formData.plan !== undefined) {
        payload.plan = parseInt(formData.plan);
      }
    }

    return JSON.stringify(payload, null, 2);
  };

  // Generate dynamic response payload
  const generateResponsePayload = (endpointId) => {
    const responses = {
      'add-partner': {
        statusCode: 200,
        code: 534,
        status: true,
        message: "Partner is created successfully",
        extensionDetails: {
          statusCode: 200,
          code: 534,
          status: true,
          message: "Extension details is updated successfully",
          payload: {
            successExtensions: formData.extensionTokens && formData.extensionTokens.length > 0 ? 
              formData.extensionTokens.map(extId => {
                const extension = extensions.find(ext => ext._id === extId);
                return {
                  _id: extId,
                  name: extension ? extension.name : 'Unknown Extension',
                  message: "Extension assigned successfully",
                  success: true,
                  isItWlOverride: false
                };
              }) : [],
            errorExtensions: [],
            partnerId: currentUser?._id || "Error - Token Missing"
          }
        },
        partner: {
          _id: currentUser?._id || "Error - Token Missing",
          email: "somePartner@example.com",
          first_name: "Gary",
          last_name: "Snails",
          phone_number: "4439798008",
          planLevel: parseInt(formData.planLevel) || 5,
          balance: formData.balance !== undefined ? formData.balance : 100,
          is_balance_unlimited: formData.is_balance_unlimited || false,
          extensionTokens: formData.extensionTokens ? formData.extensionTokens.join(',') : '',
          isAssignAll: formData.isAssignAll || false,
          createdAt: new Date().toISOString()
        }
      },
      'suspend-partner': {
        code: 1,
        message: "Partner has been suspended successfully"
      },
      'unsuspend-partner': {
        code: 1,
        message: "Partner has been unsuspended successfully"
      },
      'create-user': {
        success: true,
        message: 'User created successfully via webhook',
        user: {
          id: '507f1f77bcf86cd799439011',
          email: 'someUser@example.com',
          name: 'Gary Snails',
          phoneNumber: '2015466542',
          company: 'Acme Corp',
          extensionId: '68582ff89bf2da89391c8631',
          extensionName: 'Feather Connector',
          isActive: true,
          createdAt: '2024-01-15T10:30:00.000Z'
        }
      },
      'update-user': {
        success: true,
        message: 'User updated successfully via webhook',
        user: {
          id: '507f1f77bcf86cd799439011',
          email: 'someUser@example.com',
          name: 'Gary Snails',
          phoneNumber: '2015466542',
          company: 'Acme Corp',
          isActive: true,
          updatedAt: '2024-01-15T10:30:00.000Z'
        }
      },
      'suspend-user': {
        success: true,
        message: 'User suspended successfully via webhook',
        user: {
          id: '507f1f77bcf86cd799439011',
          email: 'someUser@example.com',
          status: 'suspended',
          isActive: false,
          suspendedAt: '2024-01-15T10:30:00.000Z'
        }
      },
      'unsuspend-user': {
        success: true,
        message: 'User unsuspended successfully via webhook',
        user: {
          id: '507f1f77bcf86cd799439011',
          email: 'someUser@example.com',
          status: 'active',
          isActive: true,
          unsuspendedAt: '2024-01-15T10:30:00.000Z'
        }
      },
      'delete-user': {
        success: true,
        message: 'User deleted successfully via webhook',
        user: {
          id: '507f1f77bcf86cd799439011',
          email: 'someUser@example.com',
          status: 'deleted',
          deletedAt: '2024-01-15T10:30:00.000Z'
        }
      },
      'get-user': {
        success: true,
        message: 'User found successfully',
        user: {
          id: '507f1f77bcf86cd799439011',
          email: 'someUser@example.com',
          name: 'Gary Snails ',
          phoneNumber: '2015466542',
          company: 'Acme Corp',
          plan: 0,
          extensionId: '68582ff89bf2da89391c8631',
          extensionName: 'Feather Connector',
          isActive: true,
          createdAt: '2024-01-15T10:30:00.000Z',
          updatedAt: '2024-01-15T10:30:00.000Z'
        }
      }
    };

    return JSON.stringify(responses[endpointId] || { success: true, message: 'Success' }, null, 2);
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(''), 2000);
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  // Check if selected extension has plans
  const getSelectedExtension = () => {
    const selectedExtId = formData.ext_id;
    return extensions.find(ext => ext._id === selectedExtId);
  };

  // Check if selected extension has plans
  const hasSelectedExtensionPlans = () => {
    const selectedExtension = getSelectedExtension();
    return selectedExtension && selectedExtension.plans && selectedExtension.plans.length > 0;
  };

  const renderFormField = (fieldName, fieldConfig) => {
    // For token fields, always use currentUser._id if available, otherwise use formData or fallback
    let value;
    if (fieldName === 'token') {
      value = currentUser?._id || formData[fieldName] || fieldConfig.value;
    } else {
      value = formData[fieldName] !== undefined ? formData[fieldName] : fieldConfig.value;
    }

    if (fieldConfig.type === 'select') {
      return (
        <div className="select">
          <select 
            value={value} 
            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            disabled={fieldConfig.disabled}
          >
            {fieldConfig.options.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
      );
    }

    if (fieldConfig.type === 'checkbox') {
      return (
        <div className="toggle">
          <div className="text">{fieldConfig.label}</div>
          <label className="box_1">
            <input 
              type="checkbox" 
              className="switch_1" 
              checked={value}
              onChange={(e) => handleFieldChange(fieldName, e.target.checked)}
              disabled={fieldConfig.disabled}
            />
            <div className="toogler"></div>
          </label>
        </div>
      );
    }

    return (
      <input 
        type={fieldConfig.type}
        placeholder={fieldConfig.placeholder} 
        value={value}
        onChange={(e) => handleFieldChange(fieldName, e.target.value)}
        disabled={fieldConfig.disabled}
        className={fieldConfig.type === 'number' ? 'selected' : ''}
      />
    );
  };

  const renderEndpointContent = () => {
    const currentEndpoint = apiEndpoints.find(ep => ep.id === activeTab);
    const formFields = getFormFields(activeTab);

    if (!currentEndpoint) {
      return (
        <div className="apiBody">
          <div className="api_head_content">
            <h3>Loading...</h3>
          </div>
        </div>
      );
    }

    return (
      <div className="apiBody">
        <div className="api_head_content">
          <h3>{currentEndpoint.name}</h3>
          <div className="side_right">
            <div className="posttype">
              <span>Method:</span>
              <div className="postBtnText">{currentEndpoint.method}</div>
            </div>
            <div className="Webhook">
              <div className="name">Webhook</div>
              <div className="code">
                <mark>{currentEndpoint.url}</mark>
              </div>
              <button 
                className="copy"
                onClick={() => copyToClipboard(currentEndpoint.url, 'webhook')}
              >
                <FiCopy />
                {copied === 'webhook' ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
        </div>

        <div className="apicontentbody">
          <div className="contentLayout">
            <div className="formSection">
              {activeTab === 'add-partner' ? (
                <>
                  {/* Row 1: Token | Email */}
                  <div className="formRow">
                    <div className="formField">
                      <div className="label">
                        <label>Token</label>
                        <span className="must">*</span>
                        <tag>String</tag>
                      </div>
                      <input 
                        type="text"
                        value={currentUser?._id || 'Error - Token Missing'}
                        disabled={true}
                        className="selected"
                      />
                    </div>
                    <div className="formField">
                      <div className="label">
                        <label>Email</label>
                        <span className="must">*</span>
                        <tag>String</tag>
                      </div>
                      <input 
                        type="email"
                        value="somePartner@example.com"
                        disabled={true}
                      />
                    </div>
                  </div>

                  {/* Row 2: First Name | Last Name */}
                  <div className="formRow">
                    <div className="formField">
                      <div className="label">
                        <label>First Name</label>
                        <tag>String</tag>
                      </div>
                      <input 
                        type="text"
                        value="Gary"
                        disabled={true}
                      />
                    </div>
                    <div className="formField">
                      <div className="label">
                        <label>Last Name</label>
                        <tag>String</tag>
                      </div>
                      <input 
                        type="text"
                        value="Snails"
                        disabled={true}
                      />
                    </div>
                  </div>

                  {/* Row 3: Phone Number | Highest Plan Level */}
                  <div className="formRow">
                    <div className="formField">
                      <div className="label">
                        <label>Phone Number</label>
                        <tag>String</tag>
                      </div>
                      <input 
                        type="text"
                        value="4439798008"
                        disabled={true}
                      />
                    </div>
                    <div className="formField">
                      <div className="label">
                        <label>Highest Plan Level</label>
                        <span className="must">*</span>
                        <tag>Select</tag>
                      </div>
                      <select 
                        value={formData.planLevel || '5'}
                        onChange={(e) => handleFieldChange('planLevel', e.target.value)}
                      >
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="4">4</option>
                        <option value="5">5</option>
                      </select>
                    </div>
                  </div>

                  {/* Row 4: Extension Tokens | Balance */}
                  <div className="formRow">
                    <div className="formField">
                      <div className="label">
                        <label>Extension Tokens</label>
                        <span className="must">*</span>
                        <tag>Select</tag>
                      </div>
                      <div className="toggle">
                        <div className="text">Assign All</div>
                        <label className="box_1">
                          <input 
                            type="checkbox" 
                            className="switch_1" 
                            checked={formData.isAssignAll || false}
                            onChange={(e) => handleFieldChange('isAssignAll', e.target.checked)}
                          />
                          <div className="toogler"></div>
                        </label>
                      </div>
                      <select 
                        multiple
                        value={formData.extensionTokens || []}
                        onChange={(e) => {
                          const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                          handleFieldChange('extensionTokens', selectedOptions);
                        }}
                      >
                        {extensions.map(extension => {
                          const isSelected = formData.extensionTokens && formData.extensionTokens.includes(extension._id);
                          return (
                            <option key={extension._id} value={extension._id} selected={isSelected}>
                              {extension.name}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    <div className="formField">
                      <div className="label">
                        <label>Balance</label>
                        <span className="must">*</span>
                        <tag>Number</tag>
                      </div>
                      <div className="toggle">
                        <div className="text">Unlimited</div>
                        <label className="box_1">
                          <input 
                            type="checkbox" 
                            className="switch_1" 
                            checked={formData.is_balance_unlimited || false}
                            onChange={(e) => handleFieldChange('is_balance_unlimited', e.target.checked)}
                          />
                          <div className="toogler"></div>
                        </label>
                      </div>
                      <input 
                        type="number"
                        value={formData.balance !== undefined ? formData.balance : 100}
                        onChange={(e) => handleFieldChange('balance', parseInt(e.target.value) || 0)}
                        className="selected"
                        disabled={formData.is_balance_unlimited}
                      />
                    </div>
                  </div>
                </>
              ) : activeTab === 'add-update-user' ? (
                <>
                  {/* Row 1: Token | Email */}
                  <div className="formRow">
                    <div className="formField">
                      <div className="label">
                        <label>Token</label>
                        <span className="must">*</span>
                        <tag>String</tag>
                      </div>
                      <input 
                        type="text"
                        value={currentUser?._id || 'Error - Token Missing'}
                        disabled={true}
                        className="selected"
                      />
                    </div>
                    <div className="formField">
                      <div className="label">
                        <label>Email</label>
                        <span className="must">*</span>
                        <tag>String</tag>
                      </div>
                      <input 
                        type="email"
                        value={formData.email || 'someUser@example.com'}
                        onChange={(e) => handleFieldChange('email', e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Row 2: First Name | Last Name */}
                  <div className="formRow">
                    <div className="formField">
                      <div className="label">
                        <label>First Name</label>
                        <span className="must">*</span>
                        <tag>String</tag>
                      </div>
                      <input 
                        type="text"
                        value={formData.first_name || 'Gary'}
                        onChange={(e) => handleFieldChange('first_name', e.target.value)}
                      />
                    </div>
                    <div className="formField">
                      <div className="label">
                        <label>Last Name</label>
                        <span className="must">*</span>
                        <tag>String</tag>
                      </div>
                      <input 
                        type="text"
                        value={formData.last_name || 'Snails'}
                        onChange={(e) => handleFieldChange('last_name', e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Row 3: Phone Number | Extension Token */}
                  <div className="formRow">
                    <div className="formField">
                      <div className="label">
                        <label>Phone Number</label>
                        <tag>String</tag>
                      </div>
                      <input 
                        type="text"
                        value={formData.phone_number || '2015466542'}
                        onChange={(e) => handleFieldChange('phone_number', e.target.value)}
                      />
                    </div>
                    <div className="formField">
                      {/* Empty field to maintain grid layout */}
                    </div>
                  </div>

                  {/* Row 4: Extension Token */}
                  <div className="formRow">
                    <div className="formField">
                      <div className="label">
                        <label>Extension Token (Extension ID)</label>
                        <span className="must">*</span>
                        <tag>Select</tag>
                      </div>
                      <select 
                        value={formData.ext_id || ''}
                        onChange={(e) => handleFieldChange('ext_id', e.target.value)}
                      >
                        <option value="">Select Extension...</option>
                        {extensions.map(extension => (
                          <option key={extension._id} value={extension._id}>
                            {extension.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="formField">
                      {/* Empty field to maintain grid layout */}
                    </div>
                  </div>

                  {/* Row 5: Plan Level (if extension has plans) */}
                  {hasSelectedExtensionPlans() && (
                    <div className="formRow">
                      <div className="formField">
                        <div className="label">
                          <label>Plan Level</label>
                          <span className="must">*</span>
                          <tag>Select</tag>
                        </div>
                        <select 
                          value={formData.plan || '0'}
                          onChange={(e) => handleFieldChange('plan', e.target.value)}
                        >
                          <option value="0">0</option>
                          <option value="1">1</option>
                          <option value="2">2</option>
                          <option value="3">3</option>
                          <option value="4">4</option>
                          <option value="5">5</option>
                        </select>
                      </div>
                      <div className="formField">
                        {/* Empty field to maintain grid layout */}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                // For other endpoints, use the original dynamic rendering
                <>
                  {Object.keys(formFields).map((fieldName, index) => {
                    const field = formFields[fieldName];
                    return (
                      <div key={fieldName} className="formField">
                        <div className="label">
                          <label>{field.label}</label>
                          {field.required && <span className="must">*</span>}
                          <tag>{field.type === 'select' || field.type === 'extension-select' ? 'Select' : field.type === 'checkbox' ? 'Boolean' : 'String'}</tag>
                        </div>
                        {renderFormField(fieldName, field)}
                      </div>
                    );
                  })}
                </>
              )}
            </div>

            <div className="codeSection">
              <div className="showCode">
                <div className="headpart">
                  <h3>Request Payload</h3>
                  <div className="type">
                    Content Type: application/json
                    <button onClick={() => copyToClipboard(generateRequestPayload(activeTab), 'request')}>
                      <FiCopy />
                    </button>
                  </div>
                </div>
                <code className="codeBody">
                  {generateRequestPayload(activeTab)}
                </code>
              </div>

              <div className="showCode staticCode">
                <div className="headpart">
                  <h3>Response Payload</h3>
                  <div className="type">
                    Content Type: application/json
                  </div>
                </div>
                <code className="codeBody">
                  {generateResponsePayload(activeTab)}
                </code>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="api-page">
      <div className="api_side_pan">
        <div className="api_headding">
          <h3>API Documentation</h3>
          <p>Please refer this docs to perform operations in Chromi.us Platform</p>
        </div>
        <ul>
          {apiEndpoints.map(endpoint => (
            <li key={endpoint.id} className={activeTab === endpoint.id ? 'active' : ''}>
              <span 
                className="tabLink"
                onClick={() => setActiveTab(endpoint.id)}
              >
                {endpoint.name}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mainInner">
        <div className="dataArea-content">
          <div className="tab tabDashboard">
            <div className="tabHeader d-flex">
              <div className="tabOpt ml-a float-right"></div>
            </div>
            <div className="tabBody">
              {renderEndpointContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default API; 